import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { CircleAlert, Eye, MousePointerClick, Percent, Users, X } from 'lucide-react';
import { StorefrontService } from '../../../core/services/storefront.service';
import { StatCard } from './StatCard';

interface Props { UserReducer: any; }

const StorefrontTrafficComponent: React.FC<Props> = ({ UserReducer }) => {
    const brand_id = UserReducer?.UserData?.brand_id;
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState<any>({});
    const [perProduct, setPerProduct] = useState<any[]>([]);
    const [trend, setTrend] = useState<any[]>([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [alert, setAlert] = useState<string | null>(null);

    const load = useCallback(() => {
        if (!brand_id) return;
        setLoading(true);
        StorefrontService.getTrafficAnalytics({ brand_id, start_date: startDate, end_date: endDate })
            .then((res: any) => {
                if (res?.status === 1 && res?.data?.rows) {
                    setSummary(res.data.rows.summary || {});
                    setPerProduct(res.data.rows.per_product || []);
                    setTrend(res.data.rows.trend || []);
                } else { setSummary({}); setPerProduct([]); setTrend([]); }
            })
            .catch(() => setAlert('Failed to load analytics'))
            .finally(() => setLoading(false));
    }, [brand_id, startDate, endDate]);

    useEffect(() => { load(); }, [load]);

    const maxViews = Math.max(1, ...trend.map(t => Number(t.views) || 0));

    return (
        <div>
            <p className="brd-store__caption">Views and outbound product clicks on your public storefront — tracked even if you haven’t installed the pixel.</p>
            <div className="brd-store-dates">
                <label>From<input type="date" className="brd-input" value={startDate} onChange={e => setStartDate(e.target.value)} /></label>
                <label>To<input type="date" className="brd-input" value={endDate} onChange={e => setEndDate(e.target.value)} /></label>
                <button type="button" className="brd-btn brd-btn--outline" onClick={load}>Apply</button>
            </div>

            {alert && (
                <div className="brd-banner brd-banner--error" role="alert">
                    <CircleAlert size={16} /><span style={{ flex: 1 }}>{alert}</span>
                    <button type="button" onClick={() => setAlert(null)} aria-label="Dismiss"><X size={14} /></button>
                </div>
            )}

            {loading ? (
                <div className="brd-center"><span className="brd-spinner" /></div>
            ) : (
                <>
                    <div className="brd-store-stats">
                        <StatCard icon={<Eye size={18} />} label="Store views" value={summary.total_views ?? 0} tone="info" />
                        <StatCard icon={<Users size={18} />} label="Unique visitors" value={summary.unique_visitors ?? 0} tone="violet" />
                        <StatCard icon={<MousePointerClick size={18} />} label="Product clicks" value={summary.total_clicks ?? 0} tone="success" />
                        <StatCard icon={<Percent size={18} />} label="Click-through rate" value={`${summary.ctr ?? 0}%`} tone="warning" />
                    </div>

                    <section className="brd-section">
                        <h3 className="brd-section__title">Views trend</h3>
                        {trend.length === 0 ? (
                            <p className="brd-store__hint">No views in this period yet.</p>
                        ) : (
                            <div className="brd-store-bars">
                                {trend.map((t, i) => (
                                    <span key={i} className="brd-store-bars__bar" title={`${t.date}: ${t.views} views, ${t.visitors} visitors`}
                                        style={{ height: `${Math.max(4, (Number(t.views) / maxViews) * 110)}px` }} />
                                ))}
                            </div>
                        )}
                    </section>

                    <section className="brd-section" style={{ padding: 0, overflow: 'hidden' }}>
                        <h3 className="brd-section__title brd-store-panel-title">Top products by clicks</h3>
                        <div className="brd-store-dtable brd-store-dtable--flush">
                            <table>
                                <thead><tr><th>Product</th><th>Link</th><th className="brd-store-dtable__num">Clicks</th></tr></thead>
                                <tbody>
                                    {perProduct.length === 0 ? (
                                        <tr><td colSpan={3} className="brd-store-dtable__empty">No products yet.</td></tr>
                                    ) : perProduct.map((p) => (
                                        <tr key={p.product_id}>
                                            <td><span className="brd-store-table__title" title={p.product_title}>{p.product_title}</span></td>
                                            <td><span className="brd-store-dtable__link" title={p.product_url}>{p.product_url}</span></td>
                                            <td className="brd-store-dtable__num"><strong>{p.clicks ?? 0}</strong></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </>
            )}
        </div>
    );
};

const mapStateToProps = (state: any) => ({ UserReducer: state.UserReducer });
export const StorefrontTraffic = connect(mapStateToProps)(StorefrontTrafficComponent);
